import {
  type Card,
  type GameAction,
  type PlayedCard,
  type PlayerView,
  type RoomSnapshot,
  type Seat,
  type Suit,
  SUIT_NAMES,
  cardLabel,
  currentWinner,
  describeResult,
  ledSuitOf,
  lowCards,
  printedSuitOf,
  sortHand,
} from '@euchre/core';
import { h } from '../dom.js';
import { IS_PREVIEW, app, rerender, rerenderAt, send, serverNow } from '../state.js';
import { cardBack, cardFace, suitGlyph } from './cards.js';
import { pick, storeDealerTalk, vegas } from './voice.js';
import { menuChoice, openFeedback, setLargePrint, setSound, settings } from './extras.js';
import { POSITION_CLASS, position, seatName, teamName } from './names.js';

/** True when this player chose the Vegas dealer voice. */
const isVegas = () => app.ui.dealerTalk === 'vegas';
/** A stable seed for the current moment of the game, so a Vegas line doesn't change on every redraw. */
const seed = (g: PlayerView, key: string) => `${key}:${g.dealNumber}:${g.version}`;

/** Version we already acted on, to swallow double clicks. */
let actedVersion = -1;

function act(g: PlayerView, action: GameAction): void {
  if (actedVersion === g.version) return;
  actedVersion = g.version;
  app.ui.alone = false;
  app.ui.farmerPick = [];
  send({ type: 'game_action', action });
}

function has(g: PlayerView, pred: (a: GameAction) => boolean): boolean {
  return g.legalActions.some(pred);
}

// ---------------------------------------------------------------------------
// Score cards — the classic "count with the fives" score keeper, as pips.
// ---------------------------------------------------------------------------

function scoreCard(room: RoomSnapshot, g: PlayerView | null, team: 0 | 1): HTMLElement {
  const score = g?.scores[team] ?? 0;
  const target = g?.rules.targetScore ?? room.rules.targetScore;
  const mine = room.you.seat !== null && room.you.seat % 2 === team;
  const tricks = g && g.trump ? g.tricksWon[team] : null;
  return h(
    'div',
    { class: `score-card team-${team}${mine ? ' mine' : ''}`, attrs: { 'aria-label': `${teamName(room, team)}: ${score} of ${target} points` } },
    h('span', { class: 'score-num', text: String(score) }),
    h(
      'span',
      { class: 'score-text' },
      h('span', { class: 'score-team', text: teamName(room, team) }),
      h('span', { class: 'score-tricks', text: tricks === null ? `of ${target} points` : `${tricks} trick${tricks === 1 ? '' : 's'} this hand` }),
    ),
  );
}

function trumpBadge(room: RoomSnapshot, g: PlayerView): HTMLElement | null {
  if (!g.trump) {
    if (g.phase === 'bidding1' || g.phase === 'deal_options')
      return h('div', { class: 'trump-badge pending' }, h('span', { class: 'trump-line', text: `${cardLabel(g.upCard)} is turned up` }));
    if (g.phase === 'bidding2')
      return h('div', { class: 'trump-badge pending' }, h('span', { class: 'trump-line', text: `${SUIT_NAMES[printedSuitOf(g.upCard)]} are out` }));
    return null;
  }
  const by = seatName(room, g.maker!);
  const notes = [`Called by ${by}`];
  if (g.makerAlone) notes.push('going alone');
  if (g.defenderAlone !== null) notes.push(`${seatName(room, g.defenderAlone)} defending alone`);
  return h(
    'div',
    { class: 'trump-badge' },
    suitGlyph(g.trump, 'trump-glyph'),
    h('span', { class: 'trump-text' }, h('strong', { text: `${SUIT_NAMES[g.trump]} are trump` }), h('span', { text: notes.join(', ') })),
  );
}

// ---------------------------------------------------------------------------
// Players around the table
// ---------------------------------------------------------------------------

function plate(room: RoomSnapshot, g: PlayerView, seat: Seat): HTMLElement {
  const pos = position(room, seat);
  const occ = room.seats[seat]?.occupant ?? null;
  const name = seatName(room, seat);
  const out = g.sittingOut.includes(seat);
  const turn = g.turn === seat;
  const badges: HTMLElement[] = [];
  if (g.dealer === seat) badges.push(h('span', { class: 'chip dealer', text: 'Dealer', title: 'Dealer' }));
  if (g.maker === seat) badges.push(h('span', { class: 'chip maker', text: g.makerAlone ? 'Called, alone' : 'Called trump' }));
  if (g.defenderAlone === seat) badges.push(h('span', { class: 'chip maker', text: 'Defending alone' }));
  if (out) badges.push(h('span', { class: 'chip out', text: 'Sitting out' }));
  if (occ?.kind === 'bot' || (occ?.kind === 'human' && occ.botControlled)) badges.push(h('span', { class: 'chip bot', text: occ.kind === 'bot' ? 'Bot' : 'Bot playing' }));

  let status = '';
  if (occ?.kind === 'human' && occ.botControlled && occ.returnBy) {
    status = 'Stepped away';
  } else if (occ?.kind === 'human' && !occ.botControlled && occ.idleTakeoverAt) {
    status = `Still there? Bot in ${secondsLeft(occ.idleTakeoverAt)}s`;
  } else if (occ?.kind === 'human' && !occ.connected && !occ.botControlled && occ.botTakeoverAt) {
    const secs = Math.max(0, Math.ceil((occ.botTakeoverAt - serverNow()) / 1000));
    status = `Disconnected — a bot plays in ${secs}s`;
    rerenderAt(Date.now() + 1000);
  } else if (turn) {
    status = occ?.kind === 'bot' || (occ?.kind === 'human' && occ.botControlled) ? 'Thinking…' : seat === room.you.seat ? 'Your turn' : 'Their turn';
  }

  const count = g.handCounts[seat];
  const backs =
    pos !== 0 && !out && g.phase !== 'hand_complete' && g.phase !== 'game_over'
      ? h('div', { class: 'backs' }, Array.from({ length: count }, () => cardBack(true)))
      : null;

  const takeSeat =
    room.you.seat === null && occ?.kind === 'bot' && room.status === 'playing'
      ? h('button', { class: 'btn small', type: 'button', text: 'Take this seat', onclick: () => send({ type: 'sit', seat }) })
      : null;

  const emote = app.emotes.filter((e) => e.seat === seat && e.until > Date.now()).at(-1);
  const bubble = emote ? null : actionBubble(g, seat);

  return h(
    'div',
    { class: `plate ${POSITION_CLASS[pos]} team-${seat % 2}${turn ? ' turn' : ''}${out ? ' out' : ''}` },
    backs,
    h('div', { class: 'nameplate' }, h('span', { class: 'pname', text: name }), h('span', { class: 'badges' }, badges)),
    status ? h('span', { class: 'pstatus', text: status }) : null,
    takeSeat,
    emote ? h('span', { class: 'emote-bubble', text: emote.emote, attrs: { 'aria-label': `${emote.name} reacted ${emote.emote}` } }) : null,
    bubble ? h('span', { class: 'pass-bubble', text: bubble, attrs: { 'aria-label': `${name}: ${bubble}` } }) : null,
  );
}

/**
 * The speech bubble over a seat (both dealer voices): "Pass" while bidding, then what the caller said once trump is
 * made: "Pick it up" for ordering up (or the dealer picking it up), or the suit they named ("Hearts").
 * It stays up until the first card is led.
 */
function actionBubble(g: PlayerView, seat: Seat): string | null {
  if (passedThisRound(g, seat)) return 'Pass';
  const beforeFirstLead = g.tricks.length === 0 && g.currentTrick.length === 0 && g.phase !== 'hand_complete' && g.phase !== 'game_over';
  if (!beforeFirstLead || g.trump === null || seat !== g.maker) return null;
  return g.callRound === 2 ? SUIT_NAMES[g.trump] : 'Pick it up';
}

/** Whether `seat` has passed in the current bidding round (shown as a "Pass" bubble until bidding ends). */
function passedThisRound(g: PlayerView, seat: Seat): boolean {
  if (g.phase !== 'bidding1' && g.phase !== 'bidding2') return false;
  const round = g.phase === 'bidding1' ? 1 : 2;
  for (let i = g.log.length - 1; i >= 0; i--) {
    const e = g.log[i]!;
    if (e.t === 'deal') return false;
    if (e.t === 'pass' && e.seat === seat && e.round === round) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Centre of the table
// ---------------------------------------------------------------------------

function trickSlots(room: RoomSnapshot, cards: PlayedCard[], winner: Seat | null, held: boolean): HTMLElement {
  return h(
    'div',
    { class: `trick${held ? ' held' : ''}` },
    cards.map((p) =>
      h(
        'div',
        { class: `slot ${POSITION_CLASS[position(room, p.seat)]}${winner === p.seat ? ' winning' : ''}` },
        cardFace(p.card, { title: `${seatName(room, p.seat)}: ${cardLabel(p.card)}` }),
      ),
    ),
    held && winner !== null
      ? h('p', {
          class: 'trick-caption',
          text: isVegas()
            ? pick(vegas.takesIt(seatName(room, winner), winner === room.you.seat), `trick:${cards.map((c) => c.card).join()}`)
            : `${seatName(room, winner)} ${winner === room.you.seat ? 'take' : 'takes'} it`,
        })
      : null,
  );
}

function centre(room: RoomSnapshot, g: PlayerView): HTMLElement {
  if (g.phase === 'deal_options' || g.phase === 'bidding1' || g.phase === 'bidding2' || (g.phase === 'defend_alone' && g.upCardStatus === 'up')) {
    const turned = g.upCardStatus === 'turned_down';
    return h(
      'div',
      { class: 'kitty' },
      h('div', { class: 'kitty-stack' }, cardBack(), cardBack(), turned ? cardBack() : cardFace(g.upCard)),
      h('p', { class: 'kitty-caption', text: turned ? `${cardLabel(g.upCard)} was turned down` : `${seatName(room, g.dealer)} turned up ${cardLabel(g.upCard)}` }),
    );
  }
  if (g.phase === 'playing' || g.phase === 'discard' || g.phase === 'defend_alone' || g.phase === 'hand_complete' || g.phase === 'game_over') {
    if (g.currentTrick.length) return trickSlots(room, g.currentTrick, currentWinner(g.currentTrick, g.trump!), false);
    const held = app.heldTrick;
    if (held && held.until > Date.now()) return trickSlots(room, held.trick.cards, held.trick.winner, true);
    return h('div', { class: 'trick' });
  }
  return h('div', { class: 'trick' });
}

// ---------------------------------------------------------------------------
// The prompt: what you can do right now (or who we're waiting for)
// ---------------------------------------------------------------------------

function aloneToggle(label: string): HTMLElement {
  return h(
    'label',
    { class: 'check alone-toggle' },
    h('input', {
      type: 'checkbox',
      checked: app.ui.alone,
      onchange: (ev) => {
        app.ui.alone = (ev.target as HTMLInputElement).checked;
        rerender();
      },
    }),
    ` ${label}`,
  );
}

/** The Vegas dealer's version of "Waiting for …" (a whole sentence). */
function vegasWaiting(room: RoomSnapshot, g: PlayerView): string {
  const w = vegas.waiting;
  if (g.phase === 'deal_options') return pick(w.deal, seed(g, 'w'));
  const t = g.turn;
  if (t === null) return '';
  const who = seatName(room, t);
  const occ = room.seats[t]?.occupant;
  const bot = occ?.kind === 'bot' || (occ?.kind === 'human' && occ.botControlled);
  switch (g.phase) {
    case 'bidding1':
    case 'bidding2':
      return pick(w.bid(who), seed(g, 'w'));
    case 'defend_alone':
      return pick(w.defend(who), seed(g, 'w'));
    case 'discard':
      return pick(w.discard(who, cardLabel(g.upCard)), seed(g, 'w'));
    case 'playing':
      return pick(bot ? w.bot(who) : w.person(who), seed(g, 'w'));
    default:
      return '';
  }
}

function waitingText(room: RoomSnapshot, g: PlayerView): string {
  const t = g.turn;
  if (g.phase === 'deal_options') return 'the deal to be checked for misdeals';
  if (t === null) return '';
  const who = seatName(room, t);
  switch (g.phase) {
    case 'bidding1':
      return `${who} to order up or pass`;
    case 'bidding2':
      return `${who} to name trump or pass`;
    case 'defend_alone':
      return `${who} to decide whether to defend alone`;
    case 'discard':
      return `${who} to discard after picking up ${cardLabel(g.upCard)}`;
    case 'playing':
      return g.currentTrick.length === 0 ? `${who} to lead` : `${who} to play`;
    default:
      return '';
  }
}

/** "Tap" on touch screens, "Click" with a mouse. */
function tapOrClick(): string {
  return window.matchMedia?.('(pointer: coarse)').matches ? 'Tap' : 'Click';
}

// ---------------------------------------------------------------------------
// Stepped away: "Are you still there?" countdown, then a bot covers the seat for a while
// ---------------------------------------------------------------------------

function myOccupant(room: RoomSnapshot) {
  const seat = room.you.seat;
  const occ = seat === null ? null : room.seats[seat]?.occupant;
  return occ?.kind === 'human' ? occ : null;
}

/** Whole seconds until an epoch-ms deadline (server clock); also redraws in a second so countdowns tick. */
function secondsLeft(at: number): number {
  rerenderAt(Date.now() + 1000);
  return Math.max(0, Math.ceil((at - serverNow()) / 1000));
}

function minSec(secs: number): string {
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/** True while you're being asked "Are you still there?" (used for the tab title too). */
export function stillThereAsked(room: RoomSnapshot): boolean {
  const o = myOccupant(room);
  return !!o && !o.botControlled && !!o.idleTakeoverAt;
}

/** True while a bot is playing your seat and you can still take it back. */
export function seatCovered(room: RoomSnapshot): boolean {
  const o = myOccupant(room);
  return !!o && o.botControlled && !!o.returnBy;
}

/** Every seat is a bot or a bot covering for someone: the server pauses the game (bots never play on their own). */
function nobodyPlaying(room: RoomSnapshot): boolean {
  return !room.seats.some((s) => s.occupant?.kind === 'human' && !s.occupant.botControlled);
}

function awayNotice(room: RoomSnapshot): HTMLElement | null {
  const o = myOccupant(room);
  if (!o || room.status !== 'playing') return null;
  if (!o.botControlled && o.idleTakeoverAt) {
    const secs = secondsLeft(o.idleTakeoverAt);
    return h(
      'div',
      { class: 'away-scrim' },
      h(
        'div',
        { class: 'away-card', attrs: { role: 'alertdialog', 'aria-modal': 'true', 'aria-labelledby': 'away-title' } },
        h('h2', { id: 'away-title', text: isVegas() ? pick(vegas.stillThere, seed(room.game!, 'away')) : 'Are you still there?' }),
        h('div', { class: 'away-count', text: String(secs), attrs: { 'aria-live': 'polite' } }),
        h('p', { text: `It's your turn. A bot will play for you in ${secs} second${secs === 1 ? '' : 's'}, so the game can keep going.` }),
        h('button', { class: 'btn primary big', type: 'button', text: "I'm here", onclick: () => send({ type: 'im_here' }) }),
      ),
    );
  }
  if (o.botControlled && o.returnBy) {
    const secs = secondsLeft(o.returnBy);
    return h(
      'div',
      { class: 'away-bar', attrs: { role: 'status' } },
      nobodyPlaying(room)
        ? h('p', {}, h('strong', { text: 'The game is paused.' }), ` Nobody is playing right now. Take your seat back within ${minSec(secs)}, or the game is cancelled.`)
        : h('p', {}, h('strong', { text: 'A bot is playing for you.' }), ` Take your seat back within ${minSec(secs)}, or the bot keeps it.`),
      h('button', { class: 'btn primary', type: 'button', text: "I'm back, give me my seat", onclick: () => send({ type: 'im_here' }) }),
    );
  }
  return null;
}

function prompt(room: RoomSnapshot, g: PlayerView): HTMLElement {
  const covered = myOccupant(room)?.botControlled === true;
  const mine = g.legalActions.length > 0 && g.turn === room.you.seat && !covered;
  if (!mine) {
    if (room.status === 'playing' && nobodyPlaying(room)) {
      return h('div', { class: 'prompt waiting' }, h('p', { text: 'Paused: nobody is playing right now. The game picks up when someone takes their seat back.' }));
    }
    if (isVegas()) {
      const line = vegasWaiting(room, g);
      return h('div', { class: 'prompt waiting' }, line ? h('p', { text: line }) : null);
    }
    const text = waitingText(room, g);
    const t = g.turn === null ? null : room.seats[g.turn]?.occupant;
    const nudge = t?.kind === 'human' && !t.botControlled && t.idleTakeoverAt && g.turn !== room.you.seat
      ? ` A bot plays for them in ${secondsLeft(t.idleTakeoverAt)}s if they don't answer.`
      : '';
    return h('div', { class: 'prompt waiting' }, text ? h('p', { text: `Waiting for ${text}…${nudge}` }) : null);
  }
  // Your turn: a bright "Your turn" badge in front of the question so it can't be missed.
  const el = myPrompt(room, g);
  el.classList.add('mine');
  const line = h('div', { class: 'prompt-line' }, h('span', { class: 'turn-banner', text: isVegas() ? pick(vegas.turnBadge, seed(g, 'badge')) : 'Your turn' }));
  const p = el.querySelector(':scope > p');
  if (p) {
    p.replaceWith(line);
    line.append(p);
  } else {
    el.prepend(line);
  }
  return el;
}

function myPrompt(room: RoomSnapshot, g: PlayerView): HTMLElement {
  const upSuit = printedSuitOf(g.upCard);
  const iAmDealer = g.dealer === room.you.seat;
  switch (g.phase) {
    case 'deal_options': {
      const canRedeal = has(g, (a) => a.type === 'redeal');
      const canSwap = has(g, (a) => a.type === 'farmer_swap');
      const picked = app.ui.farmerPick;
      const swap = g.legalActions.find(
        (a) => a.type === 'farmer_swap' && [...a.cards].sort().join() === [...picked].sort().join(),
      );
      return h(
        'div',
        { class: 'prompt' },
        h('p', {
          text: canRedeal
            ? 'No aces and no face cards — you may call for a redeal.'
            : 'You hold a farmer’s hand. Pick three nines or tens to swap for the kitty’s hidden cards, or keep your hand.',
        }),
        h(
          'div',
          { class: 'prompt-actions' },
          canRedeal ? h('button', { class: 'btn primary', type: 'button', text: 'Call a redeal', onclick: () => act(g, { type: 'redeal' }) }) : null,
          canSwap
            ? h('button', {
                class: 'btn primary',
                type: 'button',
                text: picked.length === 3 ? 'Swap these three' : `Pick ${3 - picked.length} more to swap`,
                disabled: !swap,
                onclick: () => swap && act(g, swap),
              })
            : null,
          h('button', { class: 'btn', type: 'button', text: 'Keep my hand', onclick: () => act(g, { type: 'keep_hand' }) }),
        ),
      );
    }
    case 'bidding1':
      return h(
        'div',
        { class: 'prompt' },
        h('p', {
          text: isVegas()
            ? pick(vegas.orderUp(cardLabel(g.upCard), SUIT_NAMES[upSuit], iAmDealer), seed(g, 'b1'))
            : iAmDealer
              ? `Pick up the ${cardLabel(g.upCard)} and make ${SUIT_NAMES[upSuit]} trump?`
              : `Order the dealer to pick up ${cardLabel(g.upCard)}, making ${SUIT_NAMES[upSuit]} trump?`,
        }),
        h(
          'div',
          { class: 'prompt-actions' },
          h('button', { class: 'btn primary', type: 'button', text: iAmDealer ? 'Pick it up' : 'Order it up', onclick: () => act(g, { type: 'order_up', alone: app.ui.alone }) }),
          h('button', { class: 'btn', type: 'button', text: 'Pass', onclick: () => act(g, { type: 'pass' }) }),
          aloneToggle('Go alone'),
        ),
      );
    case 'bidding2': {
      const canPass = has(g, (a) => a.type === 'pass');
      const suits = (['S', 'H', 'C', 'D'] as Suit[]).filter((s) => s !== upSuit);
      return h(
        'div',
        { class: 'prompt' },
        h('p', {
          text: isVegas()
            ? pick(canPass ? vegas.nameTrump(SUIT_NAMES[upSuit]) : vegas.stuck, seed(g, 'b2'))
            : canPass
              ? `Name trump (not ${SUIT_NAMES[upSuit]}), or pass.`
              : 'Stick the dealer: you must name trump.',
        }),
        h(
          'div',
          { class: 'prompt-actions' },
          suits.map((suit) =>
            h(
              'button',
              { class: 'btn suit-btn', type: 'button', title: `Call ${SUIT_NAMES[suit]}`, onclick: () => act(g, { type: 'call_trump', suit, alone: app.ui.alone }) },
              suitGlyph(suit),
              ` ${SUIT_NAMES[suit]}`,
            ),
          ),
          canPass ? h('button', { class: 'btn', type: 'button', text: 'Pass', onclick: () => act(g, { type: 'pass' }) }) : null,
          aloneToggle('Go alone'),
        ),
      );
    }
    case 'defend_alone':
      return h(
        'div',
        { class: 'prompt' },
        // Worded as a yes/no about going alone. "Play with my partner" was misread as
        // "do you want to play at all?" (player feedback), so neither button mentions playing or not.
        h('p', {
          text: isVegas()
            ? pick(vegas.defendAlone(seatName(room, g.maker!), SUIT_NAMES[g.trump!], g.makerAlone, g.rules.loneDefenderEuchrePoints), seed(g, 'da'))
            : `${seatName(room, g.maker!)} called ${SUIT_NAMES[g.trump!]}${g.makerAlone ? ' and is going alone' : ''}. ` +
              `Do you want to defend alone? Your partner would sit out this hand, and stopping them alone scores ${g.rules.loneDefenderEuchrePoints} points.`,
        }),
        h(
          'div',
          { class: 'prompt-actions' },
          h('button', { class: 'btn primary', type: 'button', text: 'No, play normally', onclick: () => act(g, { type: 'defend_alone', alone: false }) }),
          h('button', { class: 'btn', type: 'button', text: 'Yes, defend alone', onclick: () => act(g, { type: 'defend_alone', alone: true }) }),
        ),
      );
    case 'discard':
      return h(
        'div',
        { class: 'prompt' },
        h('p', {
          text: isVegas()
            ? pick(vegas.discard(cardLabel(g.upCard), tapOrClick()), seed(g, 'dc'))
            : `You picked up ${cardLabel(g.upCard)}. ${tapOrClick()} a card to throw away.`,
        }),
      );
    case 'playing': {
      const verb = tapOrClick();
      const playable = g.legalActions.filter((a) => a.type === 'play').length;
      const led = ledSuitOf(g.currentTrick, g.trump!);
      const v = isVegas();
      let text = v ? pick(vegas.play(verb), seed(g, 'pl')) : `${verb} a card to play it.`;
      if (g.currentTrick.length === 0) text = v ? pick(vegas.lead(verb), seed(g, 'pl')) : `You lead. ${verb} any card to play it.`;
      else if (led && playable < g.myHand.length) {
        const suit = `${SUIT_NAMES[led]}${led === g.trump ? ' (trump)' : ''}`;
        text = v
          ? pick(vegas.follow(suit, verb), seed(g, 'pl'))
          : `You must follow ${suit}. ${verb} one of the bright cards; the grey ones can’t be played.`;
      }
      return h('div', { class: 'prompt' }, h('p', { text }));
    }
    default:
      return h('div', { class: 'prompt' });
  }
}

// ---------------------------------------------------------------------------
// Your hand
// ---------------------------------------------------------------------------

function myHand(room: RoomSnapshot, g: PlayerView): HTMLElement | null {
  if (room.you.seat === null) return h('div', { class: 'hand spectating' }, h('p', { class: 'hint', text: 'You’re watching. Take a bot’s seat to join in.' }));
  if (g.sittingOut.includes(room.you.seat)) {
    return h('div', { class: 'hand out' }, h('p', { class: 'hint', text: 'Your partner is going alone — you sit this hand out.' }));
  }
  const sorted = sortHand(g.myHand, g.trump);
  const myTurn = g.turn === room.you.seat && g.legalActions.length > 0;
  const lows = g.phase === 'deal_options' && has(g, (a) => a.type === 'farmer_swap') ? lowCards(g.myHand) : [];
  const legalPlay = new Set(g.legalActions.flatMap((a) => (a.type === 'play' ? [a.card] : [])));
  const cards = sorted.map((card) => {
    if (myTurn && g.phase === 'playing') {
      const ok = legalPlay.has(card);
      return cardFace(card, { state: ok ? 'playable' : 'dimmed', onclick: ok ? () => act(g, { type: 'play', card }) : undefined, title: ok ? `Play ${cardLabel(card)}` : `${cardLabel(card)} — you must follow suit` });
    }
    if (myTurn && g.phase === 'discard') {
      return cardFace(card, { state: 'playable', onclick: () => act(g, { type: 'discard', card }), title: `Discard ${cardLabel(card)}` });
    }
    if (myTurn && lows.includes(card)) {
      const picked = app.ui.farmerPick.includes(card);
      return cardFace(card, {
        state: picked ? 'selected' : 'playable',
        title: picked ? `Keep ${cardLabel(card)}` : `Swap ${cardLabel(card)}`,
        onclick: () => {
          const p = app.ui.farmerPick;
          app.ui.farmerPick = picked ? p.filter((c) => c !== card) : p.length < 3 ? [...p, card] : p;
          rerender();
        },
      });
    }
    return cardFace(card, { state: 'plain' });
  });
  return h('div', { class: `hand${myTurn ? ' my-turn' : ''}`, attrs: { 'aria-label': 'Your hand' } }, cards);
}

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

function lastTrickPanel(room: RoomSnapshot, g: PlayerView): HTMLElement | null {
  if (!app.ui.showLastTrick) return null;
  const last = g.tricks.at(-1);
  return h(
    'div',
    { class: 'overlay-card last-trick', attrs: { role: 'dialog', 'aria-label': 'Last trick' } },
    h('h2', { text: last ? `Trick ${last.number}` : 'No tricks yet this hand' }),
    last
      ? h(
          'ul',
          { class: 'last-trick-list' },
          last.cards.map((p) =>
            h('li', { class: p.seat === last.winner ? 'won' : '' }, cardFace(p.card, { small: true }), h('span', { text: `${seatName(room, p.seat)}${p.seat === last.winner ? ' — won it' : ''}` })),
          ),
        )
      : null,
    h('button', { class: 'btn small', type: 'button', text: 'Close', onclick: () => ((app.ui.showLastTrick = false), rerender()) }),
  );
}

/** Vegas headline for a finished hand, from the viewer's side of the table. */
function vegasResult(room: RoomSnapshot, r: NonNullable<PlayerView['result']>, g: PlayerView): string {
  const r2 = vegas.result;
  const myTeam = room.you.seat === null ? null : room.you.seat % 2;
  const weMade = myTeam === r.makerTeam;
  const lines =
    r.outcome === 'euchre' ? (weMade ? r2.euchreUs : r2.euchreThem) : r.outcome === 'march' ? (weMade ? r2.marchUs : r2.marchThem) : weMade ? r2.madeUs : r2.madeThem;
  return pick(lines, `result:${g.handNumber}`);
}

function resultPanel(room: RoomSnapshot, g: PlayerView): HTMLElement | null {
  if (g.phase !== 'hand_complete' && g.phase !== 'game_over') return null;
  // The hand's last trick stays on the table for 3 seconds first, so everyone can see how the hand ended.
  if (app.heldTrick && app.heldTrick.until > Date.now()) return null;
  const r = g.result;
  const nameOf = (s: Seat) => seatName(room, s);
  const teamOf = (t: 0 | 1) => teamName(room, t);
  if (g.phase === 'game_over') {
    const w = g.winner!;
    const youWon = room.you.seat !== null && room.you.seat % 2 === w;
    return h(
      'div',
      { class: 'overlay-card result game-over', attrs: { role: 'dialog' } },
      h('h2', {
        text:
          room.you.seat === null
            ? `${teamOf(w)} win`
            : isVegas()
              ? pick(youWon ? vegas.win : vegas.lose, `over:${g.handNumber}`)
              : youWon
                ? 'You win the game'
                : 'They took this one',
      }),
      r ? h('p', { text: describeResult(r, nameOf, teamOf) }) : null,
      heckleLine(g),
      h('p', { class: 'final', text: `Final score ${g.scores[0]}–${g.scores[1]}` }),
      room.you.isHost
        ? h('button', { class: 'btn primary', type: 'button', text: 'Set up another game', onclick: () => send({ type: 'new_game' }) })
        : h('p', { class: 'hint', text: 'The host can set up another game.' }),
    );
  }
  return h(
    'div',
    { class: 'overlay-card result', attrs: { role: 'status' } },
    h('h2', { text: isVegas() && r ? vegasResult(room, r, g) : r?.outcome === 'euchre' ? 'Euchred!' : r?.outcome === 'march' ? 'A march!' : 'Hand made' }),
    r ? h('p', { text: describeResult(r, nameOf, teamOf) }) : null,
    heckleLine(g),
    h('p', { class: 'hint', text: isVegas() ? pick(vegas.dealing, `deal:${g.handNumber}`) : 'Next hand is being dealt…' }),
  );
}

/** The Vegas dealer's roast of this hand's dumbest move, if there was one (it's in the chat too). */
function heckleLine(g: PlayerView): HTMLElement | null {
  const hk = app.lastHeckle;
  if (!isVegas() || !hk || hk.deal !== g.dealNumber) return null;
  return h('p', { class: 'heckle-line' }, h('strong', { text: 'Dealer: ' }), hk.text);
}

// ---------------------------------------------------------------------------

/** You're the only person seated (everyone else is a bot), so leaving ends the game. */
function onlyPerson(room: RoomSnapshot): boolean {
  return room.seats.filter((s) => s.occupant?.kind === 'human').length === 1 && room.you.seat !== null;
}

function tableMenu(room: RoomSnapshot): HTMLElement | null {
  if (!app.ui.menuOpen) return null;
  const close = () => {
    app.ui.menuOpen = false;
    rerender();
  };
  const canLeaveSeat = room.you.seat !== null && room.status === 'playing';
  return h(
    'div',
    { class: 'menu-pop', id: 'table-menu', attrs: { role: 'menu' } },
    h('p', { class: 'menu-code' }, 'Table code ', h('strong', { text: room.code })),
    IS_PREVIEW ? h('p', { class: 'menu-code', text: 'Preview version (new look being tested)' }) : null,
    h(
      'div',
      { class: 'menu-talk', attrs: { role: 'group', 'aria-label': 'Dealer talk' } },
      h('span', { class: 'menu-talk-label', text: 'Dealer talk' }),
      (['plain', 'vegas'] as const).map((v) =>
        h('button', {
          class: `btn small talk-btn${app.ui.dealerTalk === v ? ' on' : ''}`,
          type: 'button',
          text: v === 'plain' ? 'Plain' : 'Vegas',
          attrs: { 'aria-pressed': String(app.ui.dealerTalk === v) },
          onclick: () => {
            app.ui.dealerTalk = v;
            storeDealerTalk(v);
            rerender();
          },
        }),
      ),
    ),
    menuChoice('Sounds', [['on', 'On'], ['off', 'Off']] as const, settings.sound ? 'on' : 'off', (v) => setSound(v === 'on')),
    menuChoice('Large print', [['off', 'Off'], ['on', 'On']] as const, settings.largePrint ? 'on' : 'off', (v) => setLargePrint(v === 'on')),
    h('button', { class: 'btn menu-item', type: 'button', text: 'Send feedback', attrs: { role: 'menuitem' }, onclick: openFeedback }),
    canLeaveSeat
      ? h('button', {
          class: 'btn menu-item',
          type: 'button',
          text: 'Leave my seat (a bot plays for me)',
          attrs: { role: 'menuitem' },
          onclick: () => {
            close();
            if (confirm(onlyPerson(room) ? 'Leave your seat? You’re the only person playing, so the game will end.' : 'Leave your seat? A bot will finish the game for you.')) send({ type: 'stand' });
          },
        })
      : null,
    h('button', {
      class: 'btn menu-item',
      type: 'button',
      text: 'Leave the table',
      attrs: { role: 'menuitem' },
      onclick: () => {
        close();
        const ask = onlyPerson(room) ? 'Leave the table? You’re the only person playing, so the game will end.' : 'Leave the table? A bot will finish the game for you.';
        if (room.you.seat === null || room.status !== 'playing' || confirm(ask)) {
          send({ type: 'leave_room' });
        }
      },
    }),
  );
}

export function tableScreen(room: RoomSnapshot): HTMLElement {
  const g = room.game!;
  const seats = [0, 1, 2, 3] as Seat[];
  const myTeam = (room.you.seat ?? 0) % 2 as 0 | 1;
  const unread = app.unreadChat;
  const actions = h(
    'div',
    { class: 'table-actions' },
    h('button', {
      class: 'btn small bar-btn',
      type: 'button',
      text: 'Last trick',
      disabled: g.tricks.length === 0,
      onclick: () => ((app.ui.showLastTrick = !app.ui.showLastTrick), rerender()),
    }),
    h(
      'button',
      {
        class: 'btn small bar-btn chat-toggle',
        type: 'button',
        attrs: { 'aria-controls': 'side-panel', 'aria-expanded': String(app.ui.drawerOpen) },
        onclick: () => {
          app.ui.drawerOpen = !app.ui.drawerOpen;
          app.ui.menuOpen = false;
          rerender();
        },
      },
      'Chat & log',
      unread ? h('span', { class: 'count', text: String(unread), attrs: { 'aria-label': `${unread} new` } }) : null,
    ),
    h('button', {
      class: 'btn small bar-btn',
      type: 'button',
      text: 'Menu',
      attrs: { 'aria-haspopup': 'menu', 'aria-expanded': String(app.ui.menuOpen), 'aria-controls': 'table-menu' },
      onclick: () => {
        app.ui.menuOpen = !app.ui.menuOpen;
        rerender();
      },
    }),
    tableMenu(room),
  );
  return h(
    'main',
    { class: 'table-screen' },
    h(
      'header',
      { class: 'table-bar' },
      h('div', { class: 'scores' }, scoreCard(room, g, myTeam), scoreCard(room, g, (1 - myTeam) as 0 | 1)),
      trumpBadge(room, g) ?? h('div', { class: 'trump-badge blank' }),
      actions,
    ),
    h(
      'div',
      { class: 'play-area' },
      seats.map((s) => plate(room, g, s)),
      h('div', { class: 'centre' }, centre(room, g)),
      resultPanel(room, g),
      lastTrickPanel(room, g),
    ),
    awayNotice(room) ?? prompt(room, g),
    myHand(room, g),
    app.ui.menuOpen ? h('div', { class: 'menu-scrim', onclick: () => ((app.ui.menuOpen = false), rerender()) }) : null,
    app.ui.drawerOpen ? h('div', { class: 'drawer-scrim', onclick: () => ((app.ui.drawerOpen = false), rerender()) }) : null,
  );
}
